import React, { useState, useEffect, useContext, createContext } from 'react';

const ThemeContext = createContext();

const THEME_KEY = 'theme';

function getSystemTheme() {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(preference) {
  const root = document.documentElement;
  root.classList.remove('dark');

  let resolved = preference;
  if (preference === 'system') {
    resolved = getSystemTheme();
  }

  if (resolved === 'dark') {
    root.classList.add('dark');
  }

  // Help form controls, scrollbars etc. pick native dark styles
  root.style.colorScheme = resolved;
}

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    try {
      return localStorage.getItem(THEME_KEY) || 'system';
    } catch (e) {
      return 'system';
    }
  });

  const setTheme = (newTheme) => {
    const valid = ['light', 'dark', 'system'].includes(newTheme) ? newTheme : 'system';
    setThemeState(valid);
    try {
      localStorage.setItem(THEME_KEY, valid);
    } catch (e) {}
    applyTheme(valid);
  };

  useEffect(() => {
    // Ensure correct class is applied after mount (in case of SSR or late hydration)
    applyTheme(theme);

    // Keep in sync with OS when preference is 'system'
    const media = window.matchMedia('(prefers-color-scheme: dark)');

    const handleSystemChange = () => {
      let current;
      try {
        current = localStorage.getItem(THEME_KEY) || 'system';
      } catch (e) {
        current = 'system';
      }
      if (current === 'system') {
        applyTheme('system');
      }
    };

    // Modern + legacy
    if (media.addEventListener) {
      media.addEventListener('change', handleSystemChange);
    } else if (media.addListener) {
      media.addListener(handleSystemChange);
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', handleSystemChange);
      } else if (media.removeListener) {
        media.removeListener(handleSystemChange);
      }
    };
  }, []);

  const resolvedTheme = theme === 'system' ? getSystemTheme() : theme;

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
